<template>
	<div class="container py-5">
        <h2>{{ $t('modelingResultDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('modelingResult') }}</li>
            </ol>
        </nav>
		<div v-if="pending">
            <div class="d-flex justify-content-center loading-center">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
        <div v-else>
            <h3 class="text-center mb-4">{{ $t('limits') }}</h3>
            <table class="table table-bordered" style="border-radius:5px">
                <thead>
                    <tr>
                        <th scope="col">{{ $t('limitName') }}</th>
                        <th scope="col">{{ $t('results') }}</th>
                        <th scope="col">{{ $t('conditions') }}</th>
                        <th scope="col">{{ $t('spending') }}</th>
                        <th scope="col">{{ $t('parameterandkpi') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in selection.limits" :key="i" class="align-middle">
                        <td>{{item.name}}</td>
                        <td style="text-align: center;">
                            <div class="btn-group" role="group" aria-label="Button group with nested dropdown" >
                                <button @click="exportResults(item.id)" class="btn btn-sm btn-primary">csv</button>
                                <div class="btn-group" role="group">
                                    <button id="btnGroupDrop1" type="button" class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"></button>
                                    <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                                        <li><a class="dropdown-item" @click="exportResults(item.id, 'de-AT')" >csv - AT</a></li>
                                        <li><a class="dropdown-item" @click="exportResults(item.id, 'cs-CZ')" >csv - CZ</a></li>
                                    </ul>
                                </div>
                            </div>
                        </td>
                        <td style="text-align: center;">
                            <div class="btn-group" role="group" aria-label="Button group with nested dropdown" >
                                <button @click="exportConditions(item.id)" class="btn btn-sm btn-primary">csv</button>
                                <div class="btn-group" role="group">
                                    <button id="btnGroupDrop1" type="button" class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"></button>
                                    <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                                        <li><a class="dropdown-item" @click="exportConditions(item.id, 'de-AT')" >csv - AT</a></li>
                                        <li><a class="dropdown-item" @click="exportConditions(item.id, 'cs-CZ')" >csv - CZ</a></li>
                                    </ul>
                                </div>
                            </div>
                        </td>
                        <td style="text-align: center;">
                            <div class="btn-group" role="group" aria-label="Button group with nested dropdown">
                                <button @click="exportSpending(item.id)" class="btn btn-sm btn-primary" >csv</button>
                                <div class="btn-group" role="group">
                                    <button id="btnGroupDrop1" type="button" class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"></button>
                                    <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                                        <li><a class="dropdown-item" @click="exportSpending(item.id, 'de-AT')" >csv - AT</a></li>
                                        <li><a class="dropdown-item" @click="exportSpending(item.id, 'cs-CZ')" >csv - CZ</a></li>
                                    </ul>
                                </div>
                            </div>
                        </td>
                        <td style="text-align: center;">
                            <div class="btn-group" role="group" aria-label="Button group with nested dropdown">
                                <button @click="exportParameterAndKpi(item.id)" class="btn btn-sm btn-primary" >csv</button>
                                <div class="btn-group" role="group">
                                    <button id="btnGroupDrop1" type="button" class="btn btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"></button>
                                    <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                                        <li><a class="dropdown-item" @click="exportParameterAndKpi(item.id, 'de-AT')" >csv - AT</a></li>
                                        <li><a class="dropdown-item" @click="exportParameterAndKpi(item.id, 'cs-CZ')" >csv - CZ</a></li>
                                    </ul>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="d-grid gap-2 d-md-flex mb-4" v-if="isUserAdmin">
                <button class="btn btn-sm btn-danger" @click="showResultDeleteModal = true" >{{ $t('delete')}}</button>
            </div>
            <div class="row">
                <h3 class="text-center mb-4">{{ $t('resultVisualization') }}</h3>

                <div class="d-grid gap-2 d-md-flex">
                    <RouterLink :to="'/modeling/' + this.$route.params.id + '/modelingresult/' + this.$route.params.modelingResultId + '/weightedaverage'">
                        <button class="btn btn-sm btn-primary">{{ $t('weightedAverageCharts')}}</button>
                    </RouterLink>
                    <RouterLink :to="'/modeling/' + this.$route.params.id + '/modelingresult/' + this.$route.params.modelingResultId + '/distribution'">
                        <button class="btn btn-sm btn-primary">{{ $t('distributionCharts')}}</button>
                    </RouterLink>
                    <RouterLink :to="'/modeling/' + this.$route.params.id + '/modelingresult/' + this.$route.params.modelingResultId + '/activity'">
                        <button class="btn btn-sm btn-primary">{{ $t('activityCharts')}}</button>
                    </RouterLink>
                    <RouterLink :to="'/modeling/' + this.$route.params.id + '/modelingresult/' + this.$route.params.modelingResultId + '/sumarizedcost'">
                        <button class="btn btn-sm btn-primary">{{ $t('sumarizedCostCharts')}}</button>
                    </RouterLink>
                    <RouterLink :to="'/modeling/' + this.$route.params.id + '/modelingresult/' + this.$route.params.modelingResultId + '/activityplan'">
                        <button class="btn btn-sm btn-primary">{{ $t('activityPlan')}}</button>
                    </RouterLink>
                </div>

            </div>
        </div>
        <ModelingResultDeleteModal v-if="showResultDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}` })" @closeDeleteDialog="showResultDeleteModal = false"/>
    </div>
</template>

<script>
import ModelingResultDeleteModal from "./ModelingResultDeleteModal.vue";
import AnalysisApi from "../../api/server/analysis";
import CheckIfUserIsAdmin from "../../authCache";

export default {
    name: "modelingresultdetail",
    watch: {
        $route: "requestData"
    },
    components: {
        ModelingResultDeleteModal
    },
    mounted() {
        this.requestData();
    },
    methods: {
        exportResults(limitId, language) {
            var query = "?";
            if(language) 
                query += "ci=" + language;

            var request = AnalysisApi.exportResultsCsv(this.$route.params.id, this.$route.params.modelingResultId, limitId, query);
            window.open(request.url);
        },
        exportConditions(limitId, language) {
            var query = "?";
            if(language) 
                query += "ci=" + language;

            var request = AnalysisApi.exportConditionsCsv(this.$route.params.id, this.$route.params.modelingResultId, limitId, query);
            window.open(request.url);
        },
        exportSpending(limitId, language) {
            var query = "?";
            if(language) 
                query += "ci=" + language;

            var request = AnalysisApi.exportSpendingCsv(this.$route.params.id, this.$route.params.modelingResultId, limitId, query);
            window.open(request.url);
        },
        exportParameterAndKpi(limitId, language) {
            var query = "?";
            if(language) 
                query += "ci=" + language;

            var request = AnalysisApi.exportParameterAndKpiCsv(this.$route.params.id, this.$route.params.modelingResultId, limitId, query);
            window.open(request.url);
        },
        async requestData() {
            this.isUserAdmin = await CheckIfUserIsAdmin();

            var analysisDetail = await AnalysisApi.analysisDetail(this.$route.params.id, this.$route.params.modelingResultId);
            if(analysisDetail.modelingInfo && analysisDetail.modelingInfo.selections) {
                this.modelingName = analysisDetail.modelingInfo.name;

                this.selection = analysisDetail.modelingInfo.selections.find(s => s.id == analysisDetail.selectionId);
            }

            this.pending = false;
        }
    },
    data() {
        return {
            data: {
                name: ""
            },
            selection: {
                Limits: []
            },
            pending: true,
            showResultDeleteModal: false,
            isUserAdmin: false,
            modelingName: ""
        }
    }
};
</script>